import { graphql } from "~/graphql/codegen";

// Fragments
export const AI_RESPONSE_FRAGMENT = graphql(`
  fragment AIResponse on aiAskResponse {
    messages {
      role
      content
    }
    threadID
  }
`);

export const TRIVIA_RESULT_FRAGMENT = graphql(`
  fragment TriviaResult on TriviaResult {
    id
    question
    earnedTriviaPoints
    result
    perObjective {
      objective {
        id
        name
      }
      pointsEarned
    }
    answers {
      ... on TriviaMultipleChoiceResult {
        id
        result
        isUserAnswer
        answer
        explanation
      }
    }
  }
`);

export const SHARED_HIGHLIGHT_FRAGMENT = graphql(`
  fragment SharedHighlight on SharedHighlight {
    id
    text
    image
    source
    sourceUrl
    publicUrl
  }
`);

export const RESOURCE_TRIVIA_FRAGMENT = graphql(`
  fragment ResourceTriviaFragment on ResourceTrivia {
    earnablePoints
    questions {
      ...multipleChoiceQuestion
    }
    skillsTested {
      topSkillGroup {
        name
        color
      }
      name
      description
    }
  }
`);

export const MULTIPLE_CHOICE_QUESTION_FRAGMENT = graphql(`
  fragment multipleChoiceQuestion on ResourceTriviaMultipleChoiceQuestion {
    id
    question
    timeInSeconds
    answers {
      id
      answer
    }
    userResult {
      earnedTriviaPoints
      result
      answers {
        ... on TriviaMultipleChoiceResult {
          id
          result
          isUserAnswer
          answer
          explanation
        }
      }
    }
  }
`);

export const MINDSPACE_CARD_FRAGMENT = graphql(`
  fragment MindspaceCard on Mindspace {
    id
    name
    uniqueName
    description
    images {
      medium
    }
    members(offset: 0, size: 1) {
      total
    }
    currentUserPermission {
      role
      canAddResource
    }
  }
`);

// Mutations
export const DELETE_RESOURCE_MUTATION = graphql(`
  mutation DeleteResource($id: ID!) {
    deleteResource(id: $id)
  }
`);

export const FOLLOW_RESOURCE_MUTATION = graphql(`
  mutation followResource($ids: [ID!]!) {
    followResource(ids: $ids)
  }
`);

export const UNDO_DELETE_RESOURCE_MUTATION = graphql(`
  mutation undoDeleteResource($id: ID!) {
    undoDeleteResource(id: $id)
  }
`);

export const UPDATE_RESOURCE_IMAGE_MUTATION = graphql(`
  mutation updateResourceImage($id: ID!, $url: String!) {
    updateResourceImage(id: $id, url: $url)
  }
`);

export const MINDSPACE_ADD_ARTICLES_MUTATION = graphql(`
  mutation MindspaceAddArticles(
    $mindspaceId: ID!
    $articleIds: [ID!]!
    $copyAnnotation: Boolean
  ) {
    mindspaceAddArticles(
      mindspaceId: $mindspaceId
      articleIds: $articleIds
      copyAnnotation: $copyAnnotation
    ) {
      id
    }
  }
`);

export const SHARE_RESOURCE_MUTATION = graphql(`
  mutation shareResource(
    $articleId: ID!
    $emails: [String!]!
    $shareReason: String
  ) {
    shareResource(
      articleId: $articleId
      emails: $emails
      shareReason: $shareReason
    )
  }
`);

export const RENAME_RESOURCE_MUTATION = graphql(`
  mutation renameResource($articleId: ID!, $name: String!) {
    renameResource(body: { id: $articleId, headline: $name }) {
      id
    }
  }
`);

export const REPORT_RESOURCE_MUTATION = graphql(`
  mutation reportResource($id: ID!, $type: String!, $details: String!) {
    reportResource(body: { id: $id, type: $type, details: $details })
  }
`);

export const AI_ASK_MUTATION = graphql(`
  mutation aiAsk($context: aiContext!, $query: String, $idempotencyID: ID) {
    aiAsk(context: $context, query: $query, idempotencyID: $idempotencyID) {
      ...AIResponse
    }
  }
`);

export const AI_SIMPLIFY_MUTATION = graphql(`
  mutation aiSimplify($context: aiContext!, $idempotencyID: ID) {
    aiSimplify(context: $context, idempotencyID: $idempotencyID) {
      ...AIResponse
    }
  }
`);

export const AI_SUMMARISE_MUTATION = graphql(`
  mutation aiSummarise($context: aiContext!, $idempotencyID: ID) {
    aiSummarise(context: $context, idempotencyID: $idempotencyID) {
      ...AIResponse
    }
  }
`);

export const AI_TRANSLATE_MUTATION = graphql(`
  mutation aiTranslate(
    $context: aiContext!
    $language: String!
    $idempotencyID: ID
  ) {
    aiTranslate(
      context: $context
      language: $language
      idempotencyID: $idempotencyID
    ) {
      ...AIResponse
    }
  }
`);

export const CREATE_ANNOTATION_HIGHLIGHT_MUTATION = graphql(`
  mutation createAnnotationHighlight(
    $articleId: ID!
    $htmlHighlightInput: HTMLHighlightInput
    $pdfHighlightInput: PDFHighlightInput
  ) {
    createAnnotationHighlight(
      articleId: $articleId
      htmlHighlightInput: $htmlHighlightInput
      pdfHighlightInput: $pdfHighlightInput
    ) {
      ...AnnotationHighlight
    }
  }
`);

export const ADD_SHARED_HIGHLIGHT_MUTATION = graphql(`
  mutation AddSharedHighlight(
    $text: String!
    $source: String
    $sourceUrl: String
  ) {
    addSharedHighlight(text: $text, source: $source, sourceUrl: $sourceUrl) {
      ...SharedHighlight
    }
  }
`);

export const DELETE_HIGHLIGHT_MUTATION = graphql(`
  mutation deleteHighlight($articleId: ID!, $id: ID!) {
    deleteHighlight(articleId: $articleId, id: $id)
  }
`);

export const EDIT_ANNOTATION_HIGHLIGHT_MUTATION = graphql(`
  mutation editAnnotationHighlight(
    $articleId: ID!
    $highlightId: ID!
    $htmlHighlightInput: HTMLHighlightInput
    $pdfHighlightInput: PDFHighlightInput
  ) {
    editAnnotationHighlight(
      articleId: $articleId
      highlightId: $highlightId
      htmlHighlightInput: $htmlHighlightInput
      pdfHighlightInput: $pdfHighlightInput
    ) {
      ...AnnotationHighlight
    }
  }
`);

export const SUBMIT_TRIVIA_ANSWER_MUTATION = graphql(`
  mutation submitTriviaAnswer(
    $articleID: ID!
    $questionID: ID!
    $answerID: ID!
  ) {
    submitTriviaAnswer(
      articleID: $articleID
      questionID: $questionID
      answerID: $answerID
    ) {
      ...TriviaResult
    }
  }
`);

export const TIMEOUT_TRIVIA_ANSWER_MUTATION = graphql(`
  mutation timeoutTriviaAnswer($articleID: ID!, $questionID: ID!) {
    timeoutTriviaAnswer(articleID: $articleID, questionID: $questionID) {
      ...TriviaResult
    }
  }
`);

export const UPDATE_RESOURCE_READ_STATE_MUTATION = graphql(`
  mutation UpdateResourceReadState(
    $id: ID!
    $read: Boolean!
    $readTimeSeconds: Int
  ) {
    updateResourceReadState(
      id: $id
      read: $read
      readTimeSeconds: $readTimeSeconds
    )
  }
`);

export const SAVE_ARTICLE_TAGS_MUTATION = graphql(`
  mutation saveArticleTags($articleId: ID!, $tags: [String!]!) {
    updateResourceTags(body: { id: $articleId, tags: $tags }) {
      id
    }
  }
`);

export const UPDATE_PRIVATE_NOTE_MUTATION = graphql(`
  mutation updatePrivateNote(
    $articleID: ID!
    $highlightID: ID!
    $content: String!
    $xfdfString: String
  ) {
    updatePrivateNote(
      articleID: $articleID
      highlightID: $highlightID
      content: $content
      xfdfString: $xfdfString
    ) {
      ...AnnotationHighlightComment
    }
  }
`);

export const CREATE_HTML_COMMENT_MUTATION = graphql(`
  mutation createHTMLComment(
    $articleId: ID!
    $highlightId: ID!
    $content: String!
  ) {
    createHTMLComment(
      articleId: $articleId
      highlightId: $highlightId
      content: $content
    ) {
      ...AnnotationHighlightComment
    }
  }
`);

export const CREATE_PDF_COMMENT_MUTATION = graphql(`
  mutation createPDFComment(
    $articleId: ID!
    $highlightId: ID!
    $content: String!
    $xfdfString: String!
  ) {
    createPDFComment(
      articleId: $articleId
      highlightId: $highlightId
      content: $content
      xfdfString: $xfdfString
    ) {
      ...AnnotationHighlightComment
      ...PDFComment
    }
  }
`);

export const DELETE_COMMENT_MUTATION = graphql(`
  mutation deleteComment($articleId: ID!, $id: ID!) {
    deleteComment(articleId: $articleId, id: $id)
  }
`);

// Queries
export const AI_TRANSLATE_LANGUAGES_QUERY = graphql(`
  query aiTranslateLanguages {
    aiTranslateLanguages
  }
`);

export const READ_EVIDENCING_QUERY = graphql(`
  query readEvidencing($id: ID!) {
    readResource(id: $id) {
      evidencing {
        ...evidencing
      }
      trivia {
        ...ResourceTriviaFragment
      }
    }
  }
`);

export const MINDSPACES_QUERY = graphql(`
  query Mindspaces {
    mindspaces {
      all {
        ...MindspaceCard
      }
    }
  }
`);

export const MINDSPACE_MODULES_QUERY = graphql(`
  query MindspaceModules($uniqueName: String!) {
    mindspace(identifier: $uniqueName) {
      modules {
        resources
        objectives
        events
        programs
        sandbox
      }
      programs {
        enrolled {
          total
        }
      }
    }
  }
`);
